import { useTranslation } from "react-i18next";
import {
  AsyncCallArgs,
  GridContainer,
  GridItem,
  handleSubmitValidationFail,
  InputFormField,
  isNotEmpty,
  RichTextFormField,
  useAsyncCall,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { ViewDialog, CircularLoader, UTextFormField } from "@duosoftbg/nacid-components";
import {
  createEmailTemplate,
  getEmailBaseTemplate,
  getEmailTemplate,
  updateEmailTemplate,
} from "../../../../../../axios/api/services";
import { emailTemplateInitialValues } from "../../../../../../init/emailTemplateInitialValues";
import { FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import { EmailTemplateDetails } from "../../../../../../types/emailTemplateTypes";
import { createTemplateValidationSchema } from "../../../../../../yup/schema/email/template/templateValidationSchema";
import React, { useEffect, useState } from "react";
import useAppSelector from "../../../../../../hooks/redux/base/useAppSelector";
import { closeModal } from "../../../../../../store/redux/slice/ComponentsControl/emailTemplatesControl";
import useAppDispatch from "../../../../../../hooks/redux/base/useAppDispatch";
import { Alert } from "@mui/material";

const Content = ({ loading, error, methods, id }) => {
  const { t } = useTranslation();

  if (loading) {
    return <CircularLoader />;
  }

  if (error) {
    return <Alert severity="error">{t("m.error.serverFetchingError")}</Alert>;
  }

  return (
    <FormProvider {...methods}>
      <form>
        <GridContainer spacing={3}>
          <GridItem sm={4} md={4}>
            <InputFormField required={true} fieldName={"id"} labelCode={"l.email.template.id"} isDisabled={id !== ""} />
          </GridItem>
          <GridItem sm={4} md={4}>
            <InputFormField required={true} fieldName={"name"} labelCode={"l.email.template.name"} />
          </GridItem>
          <GridItem sm={4} md={4}>
            <InputFormField required={true} fieldName={"nameEn"} labelCode={"l.email.template.nameEn"} />
          </GridItem>
          <GridItem sm={4} md={12}>
            <InputFormField required={true} fieldName={"subject"} labelCode={"l.email.template.subject"} />
          </GridItem>
          <GridItem sm={4} md={12}>
            <UTextFormField fieldName={"params"} labelCode={"l.email.template.params"} />
          </GridItem>
          <GridItem sm={12} md={12}>
            <RichTextFormField fieldName={"text"} onChangeMethod={(content) => methods.setValue("text", content)} />
          </GridItem>
        </GridContainer>
      </form>
    </FormProvider>
  );
};

const SaveEmailTemplateDialog = ({ filterFn }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { asyncCall } = useAsyncCall();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { open, id } = useAppSelector((state) => {
    return state.ComponentsControl.emailTemplatesControl.modals.edit;
  });

  const handleCloseDialog = () => {
    dispatch(closeModal({ modalType: "edit" }));
  };

  const { methods } = useReactHookForm<EmailTemplateDetails>({
    defaultValues: emailTemplateInitialValues,
    validationSchema: createTemplateValidationSchema,
  });

  useEffect(() => {
    if (open) {
      if (isNotEmpty(id)) {
        const asyncCallArgs: AsyncCallArgs = {
          promise: getEmailTemplate(id),
          onSuccess: (response) => {
            methods.reset(response);
            setLoading(false);
          },
          onError: () => {
            setError(true);
            setLoading(false);
          },
        };
        asyncCall(asyncCallArgs);
      } else {
        methods.reset(emailTemplateInitialValues);
        const asyncCallArgs: AsyncCallArgs = {
          promise: getEmailBaseTemplate(),
          onSuccess: (response) => {
            methods.setValue("text", response.text);
            methods.setValue("params", response.params);
            setLoading(false);
          },
          onError: () => {
            setError(true);
            setLoading(false);
          },
        };
        asyncCall(asyncCallArgs);
      }
    }
    // eslint-disable-next-line
  }, [id, open]);

  const onSubmit = (values) => {
    const asyncCreation: AsyncCallArgs = {
      withGlobalBackdrop: true,
      promise: id === "" ? createEmailTemplate(values) : updateEmailTemplate(values),
      reactHooksForm: { methods },
      onSuccess: () => {
        methods.reset(emailTemplateInitialValues, {
          keepIsSubmitted: true,
          keepSubmitCount: true,
        });
        toast.success(t("m.create.template.success"));
        handleCloseDialog();
        filterFn();
      },
    };
    asyncCall(asyncCreation);
  };

  return (
    <ViewDialog
      open={open}
      onClose={handleCloseDialog}
      title={id === "" ? "t.email.template.create.dialog" : id && "t.email.template.edit.dialog"}
      disableEnforceFocus
      dialogActionsSpacing={{ pr: 3 }}
      onSubmitBtnClick={
        !loading &&
        !error &&
        methods.handleSubmit(onSubmit, () => handleSubmitValidationFail(methods.formState, t, undefined))
      }
    >
      <Content methods={methods} id={id} loading={loading} error={error} />
    </ViewDialog>
  );
};

export default SaveEmailTemplateDialog;
